import { deviceDescription } from '@api/devices/identity-contracts';
import type { ArchiveBlocker, CustomerArchivePreview, CustomerDeletionPreview, RemainingKind } from '@api/customers/contracts';
import { FALCON_SENSORS_STAY } from '@api/customers/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, TriangleAlert, X } from 'lucide-react';
import { Dialog } from 'radix-ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ApiError, archiveCustomer, archiveCustomerOptions, deleteCustomer, deleteCustomerOptions, isArchiveBlocked, isCustomerEmpty, isCustomerNotEmpty,
  isStatus } from '../lib/api';
import { describeKeyError, signWithKey } from '../lib/webauthn';
import { useScope } from '../scope/scope';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/misc';
import { useToast } from '../ui/toast';

/** What each kind a customer still holds is, in plain words, and what to do about it. */
export const REMAINING_WORDS: Record<RemainingKind, string> = {
  devices: 'Devices, including removed ones: their history stays with the customer.',
  used_installers: 'Installer links that added a device.',
  staff_access: 'Staff access to this customer: change it in Settings → People.',
  staff_invitations: 'Staff invitations to this customer: revoke them in Settings → People.',
  end_users: 'End users at this customer.',
  service_desk: 'Service desk contacts or tickets.',
  policies: 'Patching and protection policy drafts or approvals.',
  customer_migration: 'A customer migration or its CrowdStrike and SentinelOne links.',
  crowdstrike: 'A CrowdStrike Falcon child, or a request for one (even one that failed).',
  falcon_alerts: 'CrowdStrike Falcon alerts.',
  remote_group: 'A WITTCO Remote device group.',
  remote_pilot: 'It is a WITTCO Remote pilot customer: the release owner takes it off the pilot list.',
  agent_elevations: 'An agent’s temporary access to it: wait for it to end, or close it in Settings → Agent access.',
  other: 'Other records.',
};

/** What stops archiving a customer (WRMM-337), in plain words, and what to do about it. */
export const BLOCKER_WORDS: Record<ArchiveBlocker, string> = {
  migration_running: 'A customer migration is running: finish or cancel it first.',
  crowdstrike_requested: 'Its CrowdStrike Falcon child is being created: wait until it is made or given up.',
  falcon_install_pending: 'A Falcon install is being monitored or needs an alert sent. Finish the work or cancel pending jobs, and check that workspace alerts can be delivered.',
  sentinelone_removal_pending: 'A SentinelOne removal is being monitored or needs an alert sent. Finish the work or cancel pending jobs, and check that workspace alerts can be delivered.',
  remote_pilot: REMAINING_WORDS.remote_pilot,
  agent_elevations: REMAINING_WORDS.agent_elevations,
};

function Blocked({ name, kinds }: { name: string; kinds: ArchiveBlocker[] }) {
  return (
    <div role="alert" className="mt-4 text-[13.5px] text-ink-2">
      <p className="font-medium text-ink">{name} can’t be deleted right now:</p>
      <ul className="mt-2 list-disc space-y-1.5 pl-5">{kinds.map(k => <li key={k}>{BLOCKER_WORDS[k]}</li>)}</ul>
    </div>
  );
}

const day = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });

/**
 * What archiving does, as the key touch approves it (WRMM-337), and a small guard (Harry, 2026-10-29: "would be smart to
 * have a small guard on it"): there is no undo, so it says so first and asks for the customer's exact name.
 */
function ArchivePreview({ customer, typed, onType }: { customer: CustomerArchivePreview; typed: string; onType: (name: string) => void }) {
  const n = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;
  const computers = customer.devices.length;
  return (
    <div className="mt-4 text-[13.5px] text-ink-2">
      <div role="alert" className="mb-3 flex gap-2 rounded-lg border border-attention/40 bg-attention/5 p-3 text-ink">
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-attention" />
        <div>
          <p className="font-medium">{computers ? `This removes the WITTCO agent from ${n(computers, 'computer')} now.` : 'It has no computers with the WITTCO agent left.'} It
            can’t be undone; the customer is archived and its data purged on {day(customer.purgeOn)}.</p>
          {customer.falconCid && <p className="mt-1">Its Falcon child stays at CrowdStrike until you request its deletion there.</p>}
          {customer.falconSensors && <p className="mt-1">{FALCON_SENSORS_STAY}</p>}
        </div>
      </div>
      <p>It has devices or history, so deleting it archives it: <span className="font-medium text-ink">{customer.name}</span> and everything
        about it disappear from every list, search, the portal and automation now, and its records are purged on {day(customer.purgeOn)}.
        Its name can be used again at once, but until then the archived customer keeps its CrowdStrike and SentinelOne links and its end
        users’ email addresses, so a new customer can’t use those. It can’t be restored.</p>
      <ul className="mt-2 list-disc space-y-1.5 pl-5">
        {customer.devices.length > 0 && <li>{customer.devices.length === 1 ? 'Its device is' : `Its ${customer.devices.length} devices are`} removed,
          as removing a device does (WITTCO Remote uninstalled, waiting jobs canceled): {customer.devices.map(d => deviceDescription(d)).join(', ')}.</li>}
        <li>{customer.sites.length === 1 ? 'Its site' : `Its ${customer.sites.length} sites`}: {customer.sites.map(s => s.name).join(', ')}.</li>
        {customer.installerLinks > 0 && <li>{n(customer.installerLinks, 'installer link')} revoked: {customer.installerLinks === 1 ? 'it stops' : 'they stop'} working.</li>}
        {customer.endUsers > 0 && <li>{n(customer.endUsers, 'end user')} turned off: they can’t sign in.</li>}
      </ul>
      {customer.falconCid && (
        <p role="note" className="mt-3 rounded-lg border border-line bg-sunken p-3">
          Its CrowdStrike Falcon child <span className="font-mono text-[12.5px] text-ink">{customer.falconCid}</span> stays under WITTCO’s parent at
          CrowdStrike until you request its deletion there; installed sensors keep billing until they’re removed. Your workspace’s alert email is told too.
        </p>
      )}
      <label className="mt-4 block text-[13px] font-medium text-ink">Type <span className="font-semibold">{customer.name}</span> to confirm
        <input value={typed} onChange={e => onType(e.target.value)} autoComplete="off" spellCheck={false} maxLength={200}
          className="mt-1 h-9 w-full rounded-lg border border-line bg-panel px-2.5 text-[13.5px] font-normal outline-none focus:border-brand/50" />
      </label>
    </div>
  );
}

const problem = (error: unknown) => !(error instanceof ApiError) ? describeKeyError(error)
  : isStatus(error, 409) ? 'Register a security key in Settings first: deleting a customer is approved with one.'
  : isStatus(error, 403) ? 'Only owners covering every customer can delete customers.'
  : isStatus(error, 404) ? 'It has already been deleted.' : 'Try again.';

/**
 * Deletes a customer, for owners covering every customer, after saying exactly what happens. An empty one (WRMM-336) is
 * deleted outright: the customer, its sites, its unused installer links and its CrowdStrike setting (off). One that has or had devices (WRMM-337) is
 * archived: its devices removed, hidden everywhere, and purged a year later. Like removing a device, either is approved
 * with the person's security key, bound to exactly what is shown here.
 */
export function DeleteCustomerDialog({ organizationId, name, open, onOpenChange }: {
  organizationId: string; name: string | undefined; open: boolean; onOpenChange: (open: boolean) => void;
}) {
  const { mspId } = useScope();
  const client = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  // Asked each time the dialog opens: what is shown is what the key touch approves.
  const preview = useQuery({ queryKey: ['customer-delete', mspId, organizationId], queryFn: () => deleteCustomerOptions(mspId, organizationId),
    enabled: open, gcTime: 0, staleTime: 0, retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false });
  const [refused, setRefused] = useState<RemainingKind[] | null>(null);
  const [typed, setTyped] = useState('');
  // Not empty: archived instead, with what that does asked for separately (its own key touch).
  const archiving = refused !== null || isCustomerNotEmpty(preview.error);
  const archivePreview = useQuery({ queryKey: ['customer-archive', mspId, organizationId], queryFn: () => archiveCustomerOptions(mspId, organizationId),
    enabled: open && archiving, gcTime: 0, staleTime: 0, retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false });
  const archive = useMutation({
    mutationFn: async () => archiveCustomer(mspId, organizationId, await signWithKey(archivePreview.data!.options), typed.trim()),
    onSuccess: archived => {
      onOpenChange(false);
      void client.invalidateQueries({ predicate: q => q.queryKey[0] === 'scope' && q.queryKey[1] === mspId });
      toast({ title: `Deleted ${archived.name}`, body: `${archived.devicesRemoved === null ? 'Its devices were removed'
        : archived.devicesRemoved === 1 ? 'Its device was removed' : `Its ${archived.devicesRemoved} devices were removed`}, and it is archived until its records are purged on `
        + `${day(archived.purgeAfter.slice(0, 10))}.${archived.falconCid ? ' Its CrowdStrike Falcon child stays at CrowdStrike until removed there.' : ''}` });
      navigate(`/w/${mspId}/customers`);
    },
    onError: error => {
      if (isArchiveBlocked(error) || isCustomerEmpty(error) || isStatus(error, 403)) void archivePreview.refetch();
      if (isCustomerEmpty(error)) { setRefused(null); void preview.refetch(); }
      if (isArchiveBlocked(error)) return;
      toast({ tone: 'warning', title: 'The customer wasn’t deleted', body: isStatus(error, 403)
        ? 'Your key touch no longer matched this customer: it changed, or the request took too long. Check what’s listed and try again.'
        : problem(error) });
    },
  });
  const remove = useMutation({
    mutationFn: async () => deleteCustomer(mspId, organizationId, await signWithKey(preview.data!.options)),
    onSuccess: deleted => {
      onOpenChange(false);
      void client.invalidateQueries({ predicate: q => q.queryKey[0] === 'scope' && q.queryKey[1] === mspId });
      const { installerLinks, crowdstrikeBox } = preview.data!.customer;
      const went = ['sites', ...(installerLinks ? ['unused installer links'] : []), ...(crowdstrikeBox ? ['CrowdStrike setting'] : [])];
      toast({ title: `Deleted ${deleted.name}`, body: `Its ${went.length > 1 ? `${went.slice(0, -1).join(', ')} and ${went.at(-1)}` : went[0]} went with it. `
        + 'The audit trail records the deletion.' });
      navigate(`/w/${mspId}/customers`);
    },
    onError: error => {
      if (isCustomerNotEmpty(error)) { setRefused(error.remaining ?? []); return; }
      // A touch that no longer matches (the customer changed, or it took too long): ask again for what it is now.
      if (isStatus(error, 403)) void preview.refetch();
      toast({ tone: 'warning', title: 'The customer wasn’t deleted', body: isStatus(error, 403)
        ? 'Your key touch no longer matched this customer: it changed, or the request took too long. Check what’s listed and try again.'
        : problem(error) });
    },
  });
  const busy = remove.isPending || archive.isPending;
  const close = (o: boolean) => { if (busy) return; onOpenChange(o); if (!o) { setRefused(null); setTyped(''); remove.reset(); archive.reset(); } };
  const shown = name ?? preview.data?.customer.name ?? 'This customer';
  const customer: CustomerDeletionPreview | undefined = archiving ? undefined : preview.data?.customer;
  const archived: CustomerArchivePreview | undefined = archiving ? archivePreview.data?.customer : undefined;
  const blocking = archiving && isArchiveBlocked(archivePreview.error) ? archivePreview.error.blocking ?? [] : null;
  const failed = archiving ? (blocking ? null : archivePreview.error) : preview.error;
  return (
    <Dialog.Root open={open} onOpenChange={close}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 animate-fade" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[90dvh] overflow-y-auto rounded-t-2xl border border-line bg-raised p-5 shadow-pop animate-pop sm:inset-auto sm:left-1/2 sm:top-[10vh] sm:w-[min(520px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:rounded-2xl">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="break-words text-[17px] font-semibold">Delete {shown}?</Dialog.Title>
              <Dialog.Description className="mt-0.5 text-[13px] text-ink-2">This can’t be undone. The audit trail keeps a record of it.</Dialog.Description>
            </div>
            <Dialog.Close className="rounded-md p-1 text-ink-3 hover:bg-hover" aria-label="Close"><X className="size-4" /></Dialog.Close>
          </div>
          {blocking ? <Blocked name={shown} kinds={blocking} />
            : failed ? <p role="alert" className="mt-4 text-[13.5px] text-ink-2">{problem(failed)}</p>
            : archived ? <ArchivePreview customer={archived} typed={typed} onType={setTyped} />
            : !customer ? <div className="mt-4 space-y-2" aria-label="Checking what this customer holds"><Skeleton className="w-2/3" /><Skeleton className="w-1/2" /></div>
            : (
              <div className="mt-4 text-[13.5px] text-ink-2">
                <p>Deleting it removes:</p>
                <ul className="mt-2 list-disc space-y-1.5 pl-5">
                  <li>The customer <span className="font-medium text-ink">{customer.name}</span>.</li>
                  <li>{customer.sites.length === 1 ? 'Its site' : `Its ${customer.sites.length} sites`}: {customer.sites.map(s => s.name).join(', ')}.</li>
                  {customer.installerLinks > 0 && <li>{customer.installerLinks} unused installer link{customer.installerLinks === 1 ? '' : 's'}: {customer.installerLinks === 1 ? 'it stops' : 'they stop'} working.</li>}
                  {customer.crowdstrikeBox && <li>Its CrowdStrike setting (off).</li>}
                </ul>
              </div>
            )}
          <div className="mt-5 flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost" disabled={busy}>{blocking || failed ? 'Close' : 'Cancel'}</Button></Dialog.Close>
            {archived && (
              <Button variant="danger" disabled={archive.isPending || archivePreview.isFetching || typed.trim() !== archived.name} onClick={() => archive.mutate()}>
                <KeyRound className="size-4" />{archive.isPending ? 'Waiting for your key…' : 'Archive with security key'}
              </Button>
            )}
            {customer && (
              <Button variant="danger" disabled={remove.isPending || preview.isFetching} onClick={() => remove.mutate()}>
                <KeyRound className="size-4" />{remove.isPending ? 'Waiting for your key…' : 'Delete with security key'}
              </Button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
